/**
 * Compact session card for the Vođene rows (APP3): image, name, duration and, for signed-in
 * users, a 44 px heart. The heart sits beside the link, not inside it, so both are real controls.
 */
import { useId } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Heart, Lock } from 'lucide-react';
import { SESSION_IMAGE_GRADIENT, SessionImageFallback } from '@/components/ui';
import { useToggleFavorite } from '@/hooks';
import { useIsAuthenticated, useLanguage, useUser } from '@/store';
import { ROUTES } from '@/config/routes';
import { cn } from '@/utils/cn';
import { formatSessionDuration } from '@/utils/sessionBrowse';
import { getSessionCardTranslation } from '@/utils/translations';
import type { LocalizedSession, Session } from '@/types';

interface SessionTileProps {
  session: Session | LocalizedSession;
  isFavorite?: boolean;
}

export function SessionTile({ session, isFavorite = false }: SessionTileProps) {
  const language = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  const user = useUser();
  const toggleFavorite = useToggleFavorite();
  const titleId = useId();
  const showLock = session.is_premium && !user?.is_premium;

  return (
    <article className="relative h-full overflow-hidden rounded-xl border bg-card text-card-foreground">
      <Link
        to={ROUTES.sessionDetail(session.id)}
        className="block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <div className={cn('relative aspect-[4/3]', SESSION_IMAGE_GRADIENT)}>
          {session.image_url ? (
            <img src={session.image_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <SessionImageFallback className="absolute inset-0" />
          )}
          {showLock && (
            <span className="absolute bottom-2 left-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white">
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">{getSessionCardTranslation('premium', language)}</span>
            </span>
          )}
        </div>
        <div className="space-y-1 p-2.5">
          <h3 id={titleId} className="line-clamp-2 text-sm font-medium leading-snug">
            {session.title}
          </h3>
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3 shrink-0" aria-hidden="true" />
            {formatSessionDuration(session.duration)}
          </p>
        </div>
      </Link>

      {isAuthenticated && (
        <button
          type="button"
          onClick={() => toggleFavorite.mutate(session.id)}
          disabled={toggleFavorite.isPending}
          aria-label={getSessionCardTranslation('favorite', language)}
          aria-describedby={titleId}
          aria-pressed={isFavorite}
          className={cn(
            'absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          <span
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full shadow-sm',
              isFavorite ? 'bg-red-500 text-white' : 'bg-white/85 text-gray-700',
            )}
          >
            <Heart className={cn('h-4 w-4', isFavorite && 'fill-current')} aria-hidden="true" />
          </span>
        </button>
      )}
    </article>
  );
}
